import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LuWebhook,
  LuCircleCheck,
  LuCircleX,
  LuExternalLink,
  LuChevronDown,
} from "react-icons/lu";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonTable } from "@/components/ui/skeleton";

export function UserWebhooksPage() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["console", "admin", "user-webhooks"],
    queryFn: () => api.adminUserWebhooks.list(),
  });
  const deliveries = useQuery({
    queryKey: ["console", "admin", "user-webhook-deliveries", expanded],
    queryFn: () => (expanded ? api.adminUserWebhooks.deliveries(expanded, 50) : Promise.reject()),
    enabled: !!expanded,
  });

  return (
    <>
      <PageHeader
        icon={LuWebhook}
        title="User webhooks"
        description="Pro/Business webhook subscriptions, last-delivery status, and recent delivery history per webhook."
      />
      <div className="p-8 space-y-6">
        {list.isLoading && <SkeletonTable rows={5} cols={4} />}
        {list.data && (
          <Card>
            <div className="divide-y divide-[var(--color-border)]">
              {list.data.webhooks.length === 0 && (
                <p className="px-5 py-6 text-sm text-[var(--color-text-muted)]">
                  No webhooks registered.
                </p>
              )}
              {list.data.webhooks.map((w) => (
                <div key={w.id}>
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === w.id ? null : w.id)}
                    className="w-full text-left px-5 py-4 hover:bg-[var(--color-elevated)]/30 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                              w.active
                                ? "bg-[rgba(66,184,131,0.1)] border-[rgba(66,184,131,0.3)] text-[#6ee7b7]"
                                : "bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-subtle)]"
                            }`}
                          >
                            {w.active ? "Active" : "Disabled"}
                          </span>
                          <span className="text-sm text-[var(--color-text)] truncate max-w-[260px]">
                            {w.user_email}
                          </span>
                          <span className="text-[10px] text-[var(--color-text-subtle)]">
                            {w.events.length} events
                          </span>
                          {w.failure_count > 0 && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-[rgba(237,66,69,0.1)] border-[rgba(237,66,69,0.3)] text-[#fca5a5]">
                              {w.failure_count} fails
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[var(--color-text-subtle)] font-mono mt-1 truncate">
                          {w.url}
                        </p>
                        {w.last_failure_reason && (
                          <p className="text-[11px] text-[#fca5a5] truncate mt-1">
                            last failure: {w.last_failure_reason}
                          </p>
                        )}
                      </div>
                      <LuChevronDown
                        className={`w-4 h-4 text-[var(--color-text-subtle)] transition-transform shrink-0 ${
                          expanded === w.id ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </button>
                  {expanded === w.id && (
                    <div className="px-5 pb-5">
                      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]">
                        {deliveries.isLoading && (
                          <p className="px-4 py-4 text-xs text-[var(--color-text-muted)]">Loading...</p>
                        )}
                        {deliveries.data?.deliveries.length === 0 && (
                          <p className="px-4 py-4 text-xs text-[var(--color-text-subtle)]">No deliveries yet.</p>
                        )}
                        <div className="divide-y divide-[var(--color-border)]">
                          {deliveries.data?.deliveries.map((d) => (
                            <div key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                {d.success ? (
                                  <LuCircleCheck className="w-3.5 h-3.5 text-[#6ee7b7] shrink-0" />
                                ) : (
                                  <LuCircleX className="w-3.5 h-3.5 text-[#fca5a5] shrink-0" />
                                )}
                                <span className="text-xs font-mono text-[var(--color-text)]">
                                  {d.event_type}
                                </span>
                                {d.response_status && (
                                  <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                                    HTTP {d.response_status}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-[11px] text-[var(--color-text-subtle)] font-mono shrink-0">
                                {d.duration_ms != null && <span>{d.duration_ms}ms</span>}
                                <span>{new Date(d.created_at).toLocaleTimeString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
